import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';

const Dashboard = () => {
    const [stats, setStats] = useState({
        pending: 0,
        todayInterventions: 0,
        revenue: 0,
        totalClients: 0
    });
    const [latestRequests, setLatestRequests] = useState([]);
    const [todayAppts, setTodayAppts] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Stats
            const pendingQuotes = await pb.collection('quotes').getList(1, 1, {
                filter: 'status = "en_attente"'
            });

            const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
            const todayApps = await pb.collection('appointments').getFullList({
                filter: `date = "${today}"`,
                sort: 'time'
            });

            const completedQuotes = await pb.collection('quotes').getFullList({
                filter: 'status = "termine"'
            });
            const totalRevenue = completedQuotes.reduce((sum, q) => sum + (q.total_price || 0), 0);

            const clients = await pb.collection('users').getList(1, 1);

            setStats({
                pending: pendingQuotes.totalItems,
                todayInterventions: todayApps.length,
                revenue: totalRevenue,
                totalClients: clients.totalItems
            });
            setTodayAppts(todayApps);

            // 2. Latest Requests
            const requests = await pb.collection('quotes').getList(1, 5, {
                sort: '-created',
                expand: 'users'
            });
            setLatestRequests(requests.items);

            // 3. Recent Activity (Latest 5 items from quotes or appointments)
            // Simplified: just latest 5 quotes for now as activity
            const activity = requests.items.map(req => ({
                id: req.id,
                type: 'new',
                text: `Nouveau devis de <strong>${req.firstName} ${req.lastName}</strong> pour une ${req.vehicle_brand}.`,
                time: new Date(req.created).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                date: new Date(req.created).toLocaleDateString()
            }));
            setRecentActivity(activity);

        } catch (err) {
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const statusBadgeMap = {
        en_attente: 'pending',
        valide: 'progress',
        diagnostic: 'progress',
        installation: 'progress',
        test: 'progress',
        pret: 'completed',
        termine: 'completed',
    };

    const statusLabelMap = {
        en_attente: 'En attente',
        valide: 'Validé',
        diagnostic: 'Diag en cours',
        installation: 'Installation',
        test: 'Tests',
        pret: 'Prêt',
        termine: 'Terminé',
    };

    if (loading) return <div className="admin-content-wrapper">Chargement des données...</div>;

    return (
        <div className="admin-content-wrapper">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Tableau de Bord</h2>
                    <p>Bienvenue dans votre espace de gestion L'Univers Diag.</p>
                </div>
                <div className="admin-header-actions">
                    <button className="secondary-button date-filter">
                        <span>Global</span>
                    </button>
                    <button className="primary-button export-btn" onClick={fetchData}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                        Actualiser
                    </button>
                </div>
            </section>

            <section className="admin-dashboard">
                {todayAppts.length > 0 && (
                    <div className="card today-urgency-card" style={{ marginBottom: '2rem', borderLeft: '4px solid #facc15' }}>
                        <div className="card-header-flex">
                            <h3>⚡ Planning du Jour ({todayAppts.length} RDV)</h3>
                        </div>
                        <div className="today-interventions-grid">
                            {todayAppts.map(appt => (
                                <div key={appt.id} className="today-appt-item">
                                    <div className="appt-time">{appt.time}</div>
                                    <div className="appt-user">
                                        <strong>{appt.firstName} {appt.lastName}</strong>
                                        <span>{appt.service}</span>
                                    </div>
                                    <div className="appt-actions">
                                        <a href={`tel:${appt.phone}`} className="action-btn confirm" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            📞 Appeler
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="admin-stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon pending-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.pending}</div>
                            <div className="stat-label">Demandes en attente</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon today-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.todayInterventions}</div>
                            <div className="stat-label">RDV aujourd'hui</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon revenue-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.revenue.toLocaleString()} €</div>
                            <div className="stat-label">Chiffre d'affaires total</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon clients-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.totalClients}</div>
                            <div className="stat-label">Clients inscrits</div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-grid-layout">
                    <div className="card full-width-card">
                        <div className="card-header-flex">
                            <h3>Dernières demandes</h3>
                        </div>
                        <div className="table-responsive">
                            <table className="data-table admin-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Véhicule</th>
                                        <th>Montant</th>
                                        <th>Date</th>
                                        <th>Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {latestRequests.map((req) => (
                                        <tr key={req.id}>
                                            <td>
                                                <div className="user-cell">
                                                    <div className="user-avatar-small">{req.firstName?.[0]}{req.lastName?.[0]}</div>
                                                    <div className="user-info">
                                                        <span className="user-name">{req.firstName} {req.lastName}</span>
                                                        <span className="user-sub">{req.email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{req.vehicle_brand} {req.vehicle_model}</td>
                                            <td><strong>{req.total_price} €</strong></td>
                                            <td>{new Date(req.created).toLocaleDateString()}</td>
                                            <td><span className={`status-badge ${statusBadgeMap[req.status]}`}>{statusLabelMap[req.status]}</span></td>
                                        </tr>
                                    ))}
                                    {latestRequests.length === 0 && (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>Aucune demande pour le moment.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="recent-activity-card">
                        <div className="card-header-flex">
                            <h3>Activité Récente</h3>
                        </div>
                        <div className="activity-list">
                            {recentActivity.map((act) => (
                                <div key={act.id} className="activity-item new">
                                    <div className="activity-dot">✦</div>
                                    <div className="activity-info">
                                        <span className="activity-text" dangerouslySetInnerHTML={{ __html: act.text }}></span>
                                        <span className="activity-time">{act.date} à {act.time}</span>
                                    </div>
                                </div>
                            ))}
                            {recentActivity.length === 0 && (
                                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Pas d'activité récente.</p>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Dashboard;
